import { getBackendClient } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import type {
  ProductKanbanStatus,
  SharedProduct,
  SharedUpdateProductPayload,
} from "@/lib/types/finance";
import { cast, toPostgrestIlikeContains } from "@/lib/utils/query-helpers";
import { PRODUCT_SELECT } from "./selects";

export interface ProductListParams {
  readonly limit?: number;
  readonly offset?: number;
  readonly purchase_order_id?: string;
  readonly search?: string;
  readonly status?: ProductKanbanStatus;
}

interface ProductPage {
  limit: number;
  offset: number;
}

interface ProductQuery<TQuery> {
  eq(column: string, value: unknown): TQuery;
  or(filters: string): TQuery;
}

function getProductPage(params?: ProductListParams): ProductPage {
  return {
    limit: Math.max(1, params?.limit ?? 100),
    offset: Math.max(0, params?.offset ?? 0),
  };
}

/** Matches the trimmed term against product name, vendor product name, and SKU. */
function applyProductSearchFilter<TQuery extends ProductQuery<TQuery>>(
  query: TQuery,
  search: string | undefined,
): TQuery {
  const trimmedSearch = search?.trim();
  if (!trimmedSearch) {
    return query;
  }

  const pattern = toPostgrestIlikeContains(trimmedSearch);
  return query.or(
    `product_name.ilike.${pattern},vendor_product_name.ilike.${pattern},sku.ilike.${pattern}`,
  );
}

function applyProductColumnFilter<TQuery extends ProductQuery<TQuery>>(
  query: TQuery,
  column: string,
  value: string | undefined,
): TQuery {
  return value ? query.eq(column, value) : query;
}

const UPDATABLE_PRODUCT_COLUMNS = [
  "product_name",
  "vendor_product_name",
  "category",
  "cost",
  "quantity",
  "kanban_status",
  "is_sample",
  "is_fronted",
  "is_miscellaneous",
  "is_service",
  "total_units_behind_sample",
  "notes",
] as const satisfies readonly (keyof SharedUpdateProductPayload)[];

type UnmappedProductColumn = Exclude<
  keyof SharedUpdateProductPayload,
  (typeof UPDATABLE_PRODUCT_COLUMNS)[number]
>;

type AssertNoUnmappedProductColumn<TColumn extends never> = TColumn;

/** Fails to compile while any editable payload key is missing from `UPDATABLE_PRODUCT_COLUMNS`. */
type _UpdatableProductColumnsAreExhaustive = AssertNoUnmappedProductColumn<UnmappedProductColumn>;

/**
 * Builds the product update from the editable-column allowlist: each allowlisted
 * column present in the payload is copied, absent keys are skipped so the stored
 * value survives, and an explicit `null` clears a nullable column. Also stamps
 * the non-payload `last_modified_by_source` column. Throws when the payload
 * carries no editable field.
 */
function buildProductUpdates(payload: SharedUpdateProductPayload): Record<string, unknown> {
  const editableUpdates: Record<string, unknown> = {};

  for (const column of UPDATABLE_PRODUCT_COLUMNS) {
    const value = payload[column];
    if (value !== undefined) {
      editableUpdates[column] = value;
    }
  }

  if (Object.keys(editableUpdates).length === 0) {
    throw new Error("No valid fields to update");
  }

  return { last_modified_by_source: "database", ...editableUpdates };
}

/**
 * Lists non-deleted products, newest first. Optional filters apply in order:
 * text search across product name, vendor product name, and SKU, then purchase
 * order, then kanban status. Returns at most `limit` rows (default 100,
 * clamped to at least one) starting at `offset`, which is clamped to zero.
 */
export async function listProducts<TProduct extends SharedProduct = SharedProduct>(
  params?: ProductListParams,
): Promise<TProduct[]> {
  const backend = getBackendClient();
  const { limit, offset } = getProductPage(params);

  const baseQuery = orderListPage(
    backend.data.from("gl_products").select(PRODUCT_SELECT).eq("is_deleted", false),
    { ascending: false, column: "created_at" },
  ).range(offset, offset + limit - 1);
  const searchedQuery = applyProductSearchFilter(baseQuery, params?.search);
  const scopedQuery = applyProductColumnFilter(
    searchedQuery,
    "purchase_order_id",
    params?.purchase_order_id,
  );
  const query = applyProductColumnFilter(scopedQuery, "kanban_status", params?.status);

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<TProduct[]>(data || []);
}

/** Applies an editable-field payload to one non-deleted product. */
export async function updateProductRecord<TProduct extends SharedProduct = SharedProduct>(
  id: string,
  payload: SharedUpdateProductPayload,
): Promise<TProduct> {
  const updates = buildProductUpdates(payload);
  const backend = getBackendClient();
  const { data, error } = await backend.data
    .from("gl_products")
    .update(updates)
    .eq("id", id)
    .eq("is_deleted", false)
    .select(PRODUCT_SELECT)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Product not found");
  }

  return cast<TProduct>(data);
}
